import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import api from "api/api";
import { showNotification } from "store/features/notificationSlice";

const ProductUsageDialog = ({ open, onClose, initialData = [] }) => {
    const dispatch = useDispatch();
    const role = useSelector((state) => state.userStore.role) || null;

    const [columns, setColumns] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData.length > 0) {
            const mappedColumns = initialData.map((item) => ({
                id: item.id,
                title: item.product_code || "",
                productUsed: item.product_used || "",
                expectedUsage: item.expected_usage || "",
                standardDeviation: item.standard_deviation || "",
                study_reference__title: item.study_reference__title || "",
                is_read_only: item.is_read_only || false,
            }));
            setColumns(mappedColumns);
        } else {
            setColumns([]);
        }
    }, [initialData]);

    const handleProducdUsageChange = (columnId, value) => {
        setColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, productUsed: value } : col)));
    };

    const handleExpectedUsageChange = (columnId, value) => {
        setColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, expectedUsage: value } : col)));
    };

    const handleStandardDeviationChange = (columnId, value) => {
        setColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, standardDeviation: value } : col)));
    };

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    // Handles the API call to update product usage data.
    const handleUpdateProductUsage = async () => {
        // Build all requests up-front
        const requests = columns.map((item) => {
            const url = process.env.REACT_APP_SERVER_PATH + `/product-usages/${item.id}/`;
            const payload = {
                product_used: item.productUsed,
                expected_usage: item.expectedUsage,
                standard_deviation: item.standardDeviation,
                product_code: item.title,
            };
            return api.put(url, payload);
        });

        try {
            await Promise.all(requests)
                .then(() => {
                    setIsSaving(false);
                    dispatch(showNotification({ severity: "success", data: "Product usage updated successfully." }));
                    onClose();
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };
    const gridStyle = {
        gridTemplateColumns: `220px repeat(${columns.length}, 1fr)`,
    };

    const isAdmin = role && role.toLowerCase() === "admin";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
            sx={{ "& .MuiDialog-paper": { borderRadius: "16px" } }}
        >
            <DialogTitle
                style={{
                    backgroundColor: "rgb(51, 94, 234, 0.08)",
                    color: "rgb(51, 94, 234)",
                    fontSize: 16,
                    fontWeight: "bold",
                    textAlign: "center",
                    padding: "16px",
                }}
            >
                PRODUCT USAGE INFORMATION
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 12, top: 12, color: "rgb(51, 94, 234)" }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <div className="usage-layout-container">
                    <div
                        className="usage-grid-header"
                        style={gridStyle}
                    >
                        <div className="header-cell label-header">Product Used Information</div>
                        {columns.map((col) => (
                            <div
                                key={col.id}
                                className="header-cell product-header"
                            >
                                {col.title}/{col.study_reference__title}
                            </div>
                        ))}
                    </div>

                    <div
                        className="usage-grid-container"
                        style={gridStyle}
                    >
                        <div className="grid-cell label-cell">Product Used</div>
                        {columns.map((col) => (
                            <div
                                key={col.id}
                                className="grid-cell data-cell"
                            >
                                <input
                                    type="number"
                                    className={!col.is_read_only && isAdmin ? "table-input editable" : "table-input"}
                                    value={col.productUsed}
                                    onChange={(e) => handleProducdUsageChange(col.id, e.target.value)}
                                    readOnly={!(isAdmin === true && col.is_read_only === false)}
                                />
                            </div>
                        ))}

                        <div className="grid-cell label-cell">Expected Usage</div>
                        {columns.map((col) => (
                            <div
                                key={col.id}
                                className="grid-cell data-cell"
                            >
                                <input
                                    type="number"
                                    className={isAdmin ? "table-input editable" : "table-input"}
                                    value={col.expectedUsage}
                                    onChange={(e) => handleExpectedUsageChange(col.id, e.target.value)}
                                    readOnly={!isAdmin}
                                />
                            </div>
                        ))}

                        <div className="grid-cell label-cell">Standard Deviation</div>
                        {columns.map((col) => (
                            <div
                                key={col.id}
                                className="grid-cell data-cell"
                            >
                                <input
                                    type="number"
                                    className={!col.is_read_only && isAdmin ? "table-input editable" : "table-input"}
                                    value={col.standardDeviation}
                                    onChange={(e) => handleStandardDeviationChange(col.id, e.target.value)}
                                    readOnly={!(isAdmin === true && col.is_read_only === false)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", p: 3, pb: 4 }}>
                <Button
                    onClick={onClose}
                    size="small"
                    className={isAdmin ? "custom-btn custom-btn-outlined" : "custom-btn me-4 bold-content disabled"}
                    variant="outlined"
                    style={{ margin: "5px 10px" }}
                    disabled={!isAdmin}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpdateProductUsage}
                    size="small"
                    className={isAdmin ? "custom-btn custom-btn-outlined" : "custom-btn me-4 bold-content disabled"}
                    variant="outlined"
                    style={{ margin: "5px 10px" }}
                    disabled={!isAdmin}
                >
                    {isSaving ? "Updating..." : "Update"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ProductUsageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialData: PropTypes.array,
};

export default ProductUsageDialog;
